import { Box, Container, Loader, Stack } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { omit } from "lodash-es";
import React, { FC, memo, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import StickyBox from "react-sticky-box";
import { useMedia } from "react-use";
import { RoutedTabPanels, useRouter } from "router";

import { FullScreenRow } from "@/components/Grid";
import { Header } from "@/components/Header";
import { ResponsiveStickyBox } from "@/components/sticky-box/ResponsiveStickyBox";
import { TabSections, TTabOption } from "@/components/Tabs";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IURLSortConfig } from "@/core/url";
import {
  fetchJobRequestFilters,
  fetchJobRequestList,
  IJobRequestFilter,
  IJobRequestURLFilter,
} from "@/data/job-requests";
import { JobRequestListDto, JobRequestListDtoPageable } from "@/data/job-requests/types/JobRequest";
import { Col, Flex, Tabs } from "@/eworkitComponents";
import { TPageableFetchFunction, useFetcher, useMetaPageableFetcher } from "@/hooks/http";
import { mapToQuery } from "@/infrastructure/Filter/service";

import {
  FilterTabButtons,
  jobRequestFiltersToQueryMapper,
  jobRequestListSearchParamsType,
  JobRequestsFilters,
  JobRequestsFiltersProvider,
  mapToJobRequestURLFilter,
} from "../Filters";
import { JobsList } from "./JobsList";
import { buildJobsTabOptions } from "./service";

export const JobsPage: FC = memo(() => {
  const { contextCompany } = useContext(UserContext);
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const breakpoint = useBreakpointIndex();

  const companyId = contextCompany?.id;
  const router = useRouter();
  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: jobRequestListSearchParamsType,
  });

  const fetchData: TPageableFetchFunction<
    JobRequestListDto,
    JobRequestListDtoPageable,
    IJobRequestURLFilter,
    IURLSortConfig[]
  > = useCallback(
    (params, options) => {
      const filters = params.filtersConfig!;
      const tmp = filters.skillIds && filters.skillIds.length > 0 ? filters.skillIds.map(item => item.value) : [];
      return fetchJobRequestList(
        {
          ...omit(params, "filtersConfig"),
          companyId,
          // TODO: mapToQuery has to be rewritten because of weak type support.
          ...mapToQuery(jobRequestFiltersToQueryMapper, {
            ...omit(filters, "skills", "skillRoleCategories", "skillIds"),
            skillRoleCategoryIcon: filters.skillRoleCategories,
            skillIds: tmp,
          } as any),
        },
        options,
      );
    },
    [companyId],
  );

  const mainFetcher = useMetaPageableFetcher(fetchData, { meta, changeMeta }, [meta, companyId]);

  const savedSearchesFetcher = useFetcher(
    fetchJobRequestFilters,
    { countMatchingElements: false, shouldPopulateSkills: true, companyId },
    [companyId],
  );

  const handleSearchSaved = useCallback(
    (filter: IJobRequestFilter) => {
      savedSearchesFetcher.fetch();

      router.replaceSearchParams({
        filtersConfig: mapToJobRequestURLFilter(filter),
        tab: `search-${filter.id.toString()}`,
      });
    },
    [router.replaceSearchParams],
  );

  const handleSearchDeleted = useCallback(
    async (id: number) => {
      savedSearchesFetcher.fetch();

      if (meta.filtersConfig.id === id) {
        changeMeta({ tab: "all-jobs" });
      }
    },
    [meta, changeMeta],
  );

  const handleJobRequestUpdate = useCallback(() => {
    mainFetcher.fetch();
  }, [mainFetcher]);

  const renderFilterTabButtons = useCallback(
    (active: boolean, savedSearch: IJobRequestFilter) => {
      return (
        <FilterTabButtons
          active={active}
          savedSearch={savedSearch}
          onSearchSaved={handleSearchSaved}
          onSearchDeleted={handleSearchDeleted}
        />
      );
    },
    [handleSearchSaved, handleSearchDeleted],
  );

  const tabOptions: TTabOption[] = useMemo(
    () => buildJobsTabOptions(t, savedSearchesFetcher.payload, false, renderFilterTabButtons),
    [t, savedSearchesFetcher.payload, renderFilterTabButtons],
  );

  const tabPanels = useMemo(
    () => ({
      default: () => (
        <Flex direction="column" padding={isSmallScreen ? [false, "xs"] : undefined}>
          <JobsList {...mainFetcher} onRecordUpdate={handleJobRequestUpdate} />
        </Flex>
      ),
    }),
    [mainFetcher],
  );

  return (
    <JobRequestsFiltersProvider>
      <Stack>
        <ResponsiveStickyBox offsetTop={73}>
          <Box backgroundColor="n25" py={["sm", null, null, "lg"]} px="sm" borderBottom="primary">
            <Container>
              <Box px={[null, null, null, "sm"]}>
                <JobRequestsFilters onSearchSaved={handleSearchSaved} onSearchDeleted={handleSearchDeleted} />
              </Box>
            </Container>
          </Box>
        </ResponsiveStickyBox>

        <Box py={["sm", null, null, "md"]} px="sm">
          <Container>
            <Box px={[null, null, null, "sm"]}>
              <FullScreenRow>
                {!isSmallScreen && (
                  <Col width={12} margin={["sm", false, false, false]}>
                    <Flex style={{ width: "100%" }}>
                      <Header color="black" weight="bold" size="md">
                        {t("JOB_REQUESTS.LIST.OPEN_JOBS", { value: mainFetcher.payload?.totalElements })}
                      </Header>
                    </Flex>
                  </Col>
                )}
                <Col width={{ xxs: 12, md: 4 }}>
                  <StickyBox style={{ zIndex: 999 }} offsetTop={breakpoint > 2 ? 270 : 90} offsetBottom={20}>
                    <Flex direction="column" innerMargin="md" padding={isSmallScreen ? [false, "xs"] : undefined}>
                      <TabSections
                        innerMargin="sm"
                        variant="vertical-icon-tab"
                        pathName="job-requests"
                        routeParam="tab"
                        options={tabOptions}
                      />
                    </Flex>
                  </StickyBox>
                </Col>
                <Col width={{ xxs: 12, md: 8 }}>
                  {isSmallScreen && (
                    <Flex direction="column" padding={["sm", "md", "md", "md"]}>
                      <Header color="black" weight="bold" size="md">
                        {t("JOB_REQUESTS.LIST.OPEN_JOBS", { value: mainFetcher.payload?.totalElements })}
                      </Header>
                    </Flex>
                  )}
                  <Tabs>
                    {!mainFetcher.payload && savedSearchesFetcher.working ? (
                      <Box alignSelf="center">
                        <Loader size={"xl"} />
                      </Box>
                    ) : (
                      <RoutedTabPanels routeParam="tab" views={tabPanels} />
                    )}
                  </Tabs>
                </Col>
              </FullScreenRow>
            </Box>
          </Container>
        </Box>
      </Stack>
    </JobRequestsFiltersProvider>
  );
});
